/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import RadioGroup from '../../../../../components/form/elements/radio-group';
import InputDate from '../../../../../components/form/elements/date';
import TextArea from '../../../../../components/form/elements/text-area';
import Text from '../../../../../components/form/elements/text';
import Select from '../../../../../components/form/elements/select';
import { useService } from '../../../../../services/services';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { map } from 'rxjs/operators';
import StatusEnum from '../enums/event-status';
import Checkbox from '../../../../../components/form/elements/checkbox';
import PeriodDates from './period-dates';
import File from '../../../../../components/form/elements/file';
import Watch from '../../../../../components/form/elements/watch';
import Show from '../../../../../components/form/elements/show';
import AnimalType from '../../../../../enums/animal-type';
import Decimal from '../../../../../components/form/elements/decimal';
import Disabled from '../../../../../components/form/elements/disabled';
import { CalendarImage } from '../../../../../assets/icons/insex';
import Animal from '../../../../../interfaces/animal';

function useFormBody(animals: Animal[] = []) {
  const { farmId } = useParams<{
    farmId: string;
    id: string;
  }>();
  const { animalEventService, animalService } = useService();

  const { t } = useTranslation();

  const transKeyPrefix = 'pages.livestock.animals_and_events.';
  const tp = useCallback(
    (key: string, params?: { [key: string]: string | number }) =>
      t(transKeyPrefix.concat(key), params),
    [t]
  );
  const animalsLengthRef = useRef(animals.length);

  useLayoutEffect(() => {
    animalsLengthRef.current = animals.length;
  }, [animals]);

  const body = useMemo(
    () => [
      RadioGroup(
        'status',
        [
          {
            label: tp('events.grazing.planned'),
            value: StatusEnum.planned,
          },
          {
            label: tp('events.grazing.completed'),
            value: StatusEnum.completed,
          },
        ],
        null,
        true
      ),
      Show(
        Watch(
          Checkbox('period', tp('animals.period'), false, '', true),
          ({ value, setValue, getValues }) => {
            const date = getValues('date');
            const dateFrom = getValues('date_from');

            if (value && !!date) {
              setValue('date', '');
            } else if (!value && dateFrom) {
              setValue('date_from', '');
              setValue('date_to', '');
            }
          }
        ),
        'event_type',
        (value) => AnimalType.insemination !== value
      ),
      Show(
        (rest) => <PeriodDates rest={rest} />,
        ['period', 'event_type'],
        (values) => {
          const [period, eventType] = values;
          return !!period && eventType === AnimalType.seeding;
        }
      ),
      Show(
        InputDate(
          'date',
          tp('events.date'),
          null,
          null,
          true,
          CalendarImage,
          true,
          true
        ),
        ['period', 'event_type'],
        (values) => {
          const [period, eventType] = values;
          return !period || eventType !== AnimalType.seeding;
        }
      ),
      Watch(
        Select('sire_id_number', {
          asyncOptions: (value, _loadedOptions, { page, getValues }) => {
            return animalService
              .index(farmId, {
                page,
                gender: 'V',
                animal_id_last4: value,
              })
              .pipe(
                map((response) => ({
                  hasMore: response.last_page > response.current_page,
                  options: response.data.map((animal) => ({
                    label: animal.animal_id,
                    value: animal.id,
                    ...animal,
                  })),
                  additional: { page: response.current_page + 1 },
                }))
              );
          },
          label: tp('events.breeding.sire_id_number'),
          column: true,
          requiredShow: true,
          creatable: true,
          clearable: true,
        }),
        ({ value, setValue, getValues }) => {
          const isManualEntry = value?.__isNew__ === true;
          if (value && !isManualEntry) {
            setValue('sire_name', value.name || '', {
              shouldValidate: true,
              shouldDirty: true,
            });
            setValue('sire_breed', value.breed_code || '', {
              shouldValidate: true,
              shouldDirty: true,
            });
          }
          setValue('is_manual_entry', isManualEntry || false, {
            shouldValidate: true,
            shouldDirty: true,
          });
        }
      ),
      Disabled(
        Text('sire_name', tp('events.breeding.sire_name'), null, null, true),
        {
          watchOn: 'is_manual_entry',
          callback: (isManualEntry) => !isManualEntry,
        }
      ),
      Disabled(
        Text('sire_breed', tp('events.breeding.sire_breed'), null, null, true),
        {
          watchOn: 'is_manual_entry',
          callback: (isManualEntry) => !isManualEntry,
        }
      ),
      Show(
        Text(
          'artificial_insemination_practitioner',
          tp('events.breeding.artificial_insemination_practitioner'),
          null,
          null,
          true
        ),
        'event_type',
        (value) => AnimalType.seeding !== value
      ),
      Show(
        Text(
          'certificate_number',
          tp('events.breeding.certificate_number'),
          null,
          null,
          true
        ),
        'event_type',
        (value) => AnimalType.seeding !== value
      ),
      Show(
        Decimal('costs', tp('events.breeding.cost'), null, false, true, true),
        'event_type',
        (value) => AnimalType.seeding !== value
      ),
      TextArea('notes', tp('events.notes'), undefined, false, undefined, true),
      File(
        'file',
        null,
        false,
        animalEventService,
        true,
        transKeyPrefix.concat('animals.')
      ),
    ],
    [t, farmId, animalService] // Ensure this hook re-runs with necessary dependencies
  );

  return body;
}

export default useFormBody;
